import type { TableProps } from 'antd';
import { Table, Tag } from 'antd';
import { KeyboardEvent } from 'react';

import { fullName, initials, roleLabel } from '@utils';

import { AccessMapView, CompanyGroup, EmployeeGroup } from './aggregate';
import * as S from './styled';

type Props = {
  view: AccessMapView;
  employeeGroups: EmployeeGroup[];
  companyGroups: CompanyGroup[];
  isLoading: boolean;
  onSelectEmployee: (group: EmployeeGroup) => void;
  onSelectCompany: (group: CompanyGroup) => void;
};

const AccessMapTable = ({
  view,
  employeeGroups,
  companyGroups,
  isLoading,
  onSelectEmployee,
  onSelectCompany,
}: Props) => {
  // Rows are clickable, so make them focusable and activatable by keyboard too.
  const rowActivation = (onActivate: () => void) => ({
    onClick: onActivate,
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onActivate();
      }
    },
    tabIndex: 0,
  });

  const employeeColumns: TableProps<EmployeeGroup>['columns'] = [
    {
      title: <S.ColumnTitle>Employee</S.ColumnTitle>,
      key: 'employee',
      render: (_, group) => (
        <S.MemberCell align="center" gap={12}>
          <S.Avatar align="center" justify="center">
            {initials(group)}
          </S.Avatar>
          <S.MemberCell vertical gap={2}>
            <S.Name>{fullName(group)}</S.Name>
            <S.Email>{group.email}</S.Email>
          </S.MemberCell>
        </S.MemberCell>
      ),
    },
    {
      title: <S.ColumnTitle>Role</S.ColumnTitle>,
      key: 'role',
      render: (_, group) => (
        <Tag bordered={false}>{roleLabel(group.adminRole)}</Tag>
      ),
    },
    {
      title: <S.ColumnTitle>Clients</S.ColumnTitle>,
      key: 'clients',
      render: (_, group) => (
        <S.MemberCell align="center" gap={8} wrap="wrap">
          <Tag bordered={false}>{group.memberships.length}</Tag>
          {group.memberships.map((membership) => (
            <Tag key={membership.companyId} bordered={false}>
              {membership.companyName}
            </Tag>
          ))}
        </S.MemberCell>
      ),
    },
  ];

  const companyColumns: TableProps<CompanyGroup>['columns'] = [
    {
      title: <S.ColumnTitle>Company</S.ColumnTitle>,
      key: 'company',
      render: (_, group) => <S.Name>{group.companyName}</S.Name>,
    },
    {
      title: <S.ColumnTitle>Team members</S.ColumnTitle>,
      key: 'members',
      render: (_, group) => (
        <S.MemberCell align="center" gap={8} wrap="wrap">
          <Tag bordered={false}>{group.memberships.length}</Tag>
          {group.memberships.map((membership) => (
            <Tag key={membership.userId} bordered={false}>
              {fullName(membership)} · {roleLabel(membership.adminRole)}
            </Tag>
          ))}
        </S.MemberCell>
      ),
    },
  ];

  if (view === 'employee') {
    return (
      <Table
        columns={employeeColumns}
        dataSource={employeeGroups}
        loading={isLoading}
        pagination={{ pageSize: 25, hideOnSinglePage: true }}
        rowKey={(group) => group.userId}
        onRow={(group) => rowActivation(() => onSelectEmployee(group))}
        rowClassName={() => 'access-map-row'}
      />
    );
  }

  return (
    <Table
      columns={companyColumns}
      dataSource={companyGroups}
      loading={isLoading}
      pagination={{ pageSize: 25, hideOnSinglePage: true }}
      rowKey={(group) => group.companyId}
      onRow={(group) => rowActivation(() => onSelectCompany(group))}
      rowClassName={() => 'access-map-row'}
    />
  );
};

export default AccessMapTable;
